import { useEffect, useId, useState } from 'react'

import type { CatalogListDto } from '../api/catalogLists'
import { SelectCombobox } from './SelectCombobox'

type CatalogBulkListDialogProps = {
  open: boolean
  selectedCount: number
  lists: CatalogListDto[]
  busy?: boolean
  onClose: () => void
  onAdd: (listId: number) => Promise<void>
}

/** Adds the selected table rows to one shared organization list. */
export const CatalogBulkListDialog = ({
  open,
  selectedCount,
  lists,
  busy = false,
  onClose,
  onAdd,
}: CatalogBulkListDialogProps) => {
  const titleId = useId()
  const [listId, setListId] = useState('')
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setListId('')
      setError(null)
      return
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !adding) {
        event.preventDefault()
        onClose()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [adding, onClose, open])

  if (!open) return null

  const handleAdd = async () => {
    if (!listId || adding || busy) return
    setAdding(true)
    setError(null)
    try {
      await onAdd(Number(listId))
      onClose()
    } catch (addError) {
      setError(addError instanceof Error ? addError.message : 'Не удалось добавить позиции в список')
    } finally {
      setAdding(false)
    }
  }

  return (
    <div
      className="market-dialog-backdrop"
      role="presentation"
      onMouseDown={adding || busy ? undefined : onClose}
    >
      <section
        className="market-dialog market-catalog-bulk-list-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="market-dialog__header">
          <div>
            <h2 id={titleId} className="market-dialog__title">Добавить в список</h2>
            <p className="market-dialog__hint">Выбрано позиций: {selectedCount}</p>
          </div>
          <button
            type="button"
            className="echat-btn echat-btn--ghost echat-btn--sm"
            disabled={adding || busy}
            onClick={onClose}
          >
            Закрыть
          </button>
        </div>

        <SelectCombobox
          className="market-catalog-bulk-list-dialog__field"
          label="Список"
          value={listId}
          options={[
            { value: '', label: 'Выберите список' },
            ...lists.map((list) => ({ value: String(list.id), label: list.name })),
          ]}
          onChange={setListId}
          disabled={busy || adding || lists.length === 0}
        />

        {error ? <p className="echat-error" role="alert">{error}</p> : null}

        <div className="market-catalog-bulk-list-dialog__actions">
          <button
            type="button"
            className="echat-btn echat-btn--ghost"
            disabled={adding || busy}
            onClick={onClose}
          >
            Отмена
          </button>
          <button
            type="button"
            className="echat-btn echat-btn--primary"
            disabled={!listId || adding || busy || selectedCount === 0}
            onClick={() => void handleAdd()}
          >
            {adding || busy ? 'Добавляем…' : 'Добавить позиции'}
          </button>
        </div>
      </section>
    </div>
  )
}
